import { gsap, ScrollTrigger, ScrollToPlugin } from "@gsap";

/**
 * Layout: Pointers
 * Slug: pointers
 */

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const OVERLAY_START = 0.6;
const OVERLAY_END = 0.2;

// Same idea as hero_home, in SVG units: top-right, top-left, bottom-left, bottom-right
const CORNERS = [
    { x: 150, y: -150 },
    { x: -150, y: 150 },
    { x: -150, y: 150 },
    { x: 150, y: 150 },
];
const corner = (i) => CORNERS[i % CORNERS.length];

const initPointers = () => {
    const motion = gsap.matchMedia();

    motion.add("(prefers-reduced-motion: no-preference)", () => {
        const cleanups = [];

        document.querySelectorAll(".pointers-layout").forEach((layout) => {
            const stage = layout.querySelector(".pointers-stage");
            const overlay = layout.querySelector(".pointers-overlay");
            const slides = layout.querySelectorAll(".pointer-slide");
            const dots = layout.querySelectorAll(".pointer-dot");
            const steps = slides.length - 1;

            if (!stage || steps < 1) return;

            layout.classList.add("is-pinned");

            gsap.set(slides, { autoAlpha: 0 });
            gsap.set(slides[0], { autoAlpha: 1 });
            gsap.set(overlay, { opacity: OVERLAY_START });

            // Every shape starts with its segments off in the corners
            const shapes = [...layout.querySelectorAll(".shape")].map((svg) => [...svg.querySelectorAll("path")]);
            const allPaths = shapes.flat();
            shapes.forEach((paths) => paths.forEach((path, i) => gsap.set(path, { ...corner(i), opacity: 0 })));

            const intro = gsap.timeline({ scrollTrigger: { trigger: stage, start: "top 80%", once: true } });
            shapes[0]?.forEach((path, i) => {
                intro.to(path, { x: 0, y: 0, opacity: 1, duration: 1.5, ease: "power3.out" }, i * 0.15);
            });

            const setActive = (index) => {
                dots.forEach((dot, i) => dot.classList.toggle("is-active", i === index));
            };
            setActive(0);

            // Each step is 1 unit long; the crossfade happens in its middle half
            const tl = gsap.timeline({
                defaults: { ease: "none" },
                scrollTrigger: {
                    trigger: stage,
                    start: "top top",
                    end: () => `+=${steps * window.innerHeight}`,
                    pin: true,
                    scrub: 0.6,
                    snap: { snapTo: 1 / steps, duration: { min: 0.2, max: 0.6 }, ease: "power1.inOut" },
                    invalidateOnRefresh: true,
                    onUpdate: (self) => setActive(Math.round(self.progress * steps)),
                },
            });

            for (let i = 1; i <= steps; i++) {
                const at = i - 1 + 0.25;
                tl.to(slides[i - 1], { autoAlpha: 0, y: -40, duration: 0.5 }, at)
                    .fromTo(slides[i], { autoAlpha: 0, y: 40 }, { autoAlpha: 1, y: 0, duration: 0.5 }, at)
                    .to(overlay, { opacity: OVERLAY_START - ((OVERLAY_START - OVERLAY_END) * i) / steps, duration: 0.5 }, at);

                // Segments of the old shape leave to the corners, then the new ones come in
                const stepStart = i - 1 + 0.1;
                shapes[i - 1]?.forEach((path, p) => {
                    tl.fromTo(
                        path,
                        { x: 0, y: 0, opacity: 1 },
                        { ...corner(p), opacity: 0, duration: 0.3, ease: "power3.in", immediateRender: false },
                        stepStart + p * 0.04,
                    );
                });
                shapes[i]?.forEach((path, p) => {
                    tl.fromTo(
                        path,
                        { ...corner(p), opacity: 0 },
                        { x: 0, y: 0, opacity: 1, duration: 0.45, ease: "power3.out", immediateRender: false },
                        stepStart + 0.25 + p * 0.06,
                    );
                });
            }
            tl.to({}, { duration: 0 }, steps);

            const onDotClick = (index) => {
                const st = tl.scrollTrigger;
                gsap.to(window, {
                    scrollTo: st.start + (index / steps) * (st.end - st.start),
                    duration: 0.8,
                    ease: "power2.inOut",
                });
            };

            const handlers = [...dots].map((dot, i) => {
                const handler = () => onDotClick(i);
                dot.addEventListener("click", handler);
                return () => dot.removeEventListener("click", handler);
            });

            cleanups.push(() => {
                handlers.forEach((remove) => remove());
                layout.classList.remove("is-pinned");
                gsap.set([...slides, overlay, ...allPaths], { clearProps: "all" });
            });
        });

        return () => cleanups.forEach((cleanup) => cleanup());
    });
};

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initPointers);
} else {
    initPointers();
}
